import {
  buildGenderedAvatarDeck,
  type SocialProofAvatar,
} from "@/features/avatars/social-proof-avatar-deck";

const SIGNAL_CARD_AVATAR_COUNT = 5;
const FEMALE_SEED_SALT = 0xdeadbeef;
const SECONDARY_MALE_SEED_SALT = 0x9e3779b9;

export function pickSignalCardAvatars(
  seed: number,
): readonly SocialProofAvatar[] {
  const maleCount = seed % 2 === 0 ? 3 : 2;
  const femaleCount = SIGNAL_CARD_AVATAR_COUNT - maleCount;
  const male = buildGenderedAvatarDeck({
    seed,
    count: maleCount,
    gender: "male",
  });
  const female = buildGenderedAvatarDeck({
    seed: mix32(seed, FEMALE_SEED_SALT),
    count: femaleCount,
    gender: "female",
  });
  const deck = interleaveHumanSignalDeck(seed, male, female);
  assertUniqueSignalDeck(deck, seed);
  return deck;
}

function interleaveHumanSignalDeck(
  seed: number,
  male: readonly SocialProofAvatar[],
  female: readonly SocialProofAvatar[],
): readonly SocialProofAvatar[] {
  const startsWithMale = random01(mix32(seed, SECONDARY_MALE_SEED_SALT)) >= 0.5;
  const primary = startsWithMale ? male : female;
  const secondary = startsWithMale ? female : male;
  const deck: SocialProofAvatar[] = [];
  for (let index = 0; deck.length < SIGNAL_CARD_AVATAR_COUNT; index += 1) {
    const nextPrimary = primary[index];
    const nextSecondary = secondary[index];
    if (nextPrimary) deck.push(nextPrimary);
    if (nextSecondary && deck.length < SIGNAL_CARD_AVATAR_COUNT) {
      deck.push(nextSecondary);
    }
  }
  return deck;
}

function assertUniqueSignalDeck(
  deck: readonly SocialProofAvatar[],
  seed: number,
): void {
  const srcs = new Set(deck.map((avatar) => avatar.src));
  if (deck.length !== SIGNAL_CARD_AVATAR_COUNT || srcs.size !== deck.length) {
    throw new Error(
      `signal avatar deck seed=${seed} expected 5 unique srcs, got ${srcs.size}`,
    );
  }
}

function mix32(seed: number, salt: number): number {
  let value = (seed ^ salt) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x7feb352d);
  value = Math.imul(value ^ (value >>> 15), 0x846ca68b);
  return (value ^ (value >>> 16)) >>> 0;
}

function random01(seed: number): number {
  let value = (seed + 0x6d2b79f5) >>> 0;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
}
